import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useModal } from '../../hooks/modalContext';
import { useAuth } from '../../../auth/Auth';
import { useTransactions } from './hooks/useTransactions';
import { useTransactionTimeline } from './hooks/useTransactionTimeline';
import { TransactionData } from './utils/transactionUtils';
import { ModalHeader } from './components/ModalHeader';
import { TransactionList } from './components/TransactionList';
import { TransactionDetails } from './components/TransactionDetails';
import { EmptyState } from './components/EmptyState';

const PastTransactions: React.FC = () => {
  const { setModal, modalState } = useModal();
  const tenantTransactionId = modalState?.tenantTransactionId;
  const { auth } = useAuth();
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionData | null>(null);

  const { transactions, isLoading, leftSideRef, refetch } = useTransactions({
    authToken: auth?.data.token,
    tenantTransactionId,
  });

  const { timeline, isLoading: timelineLoading, fetchTimeline } = useTransactionTimeline({
    authToken: auth?.data.token,
    selectedTransaction,
  });

  // Select initial transaction
  useEffect(() => {
    if (transactions.length > 0) {
      const shouldReselect =
        !selectedTransaction ||
        (tenantTransactionId && selectedTransaction.transactionId !== tenantTransactionId);

      if (shouldReselect) {
        const toSelect = tenantTransactionId
          ? transactions.find((t) => t.transactionId === tenantTransactionId)
          : transactions[0];

        if (toSelect) {
          setSelectedTransaction(toSelect);
          fetchTimeline(toSelect.transactionId);
        }
      }
    }
  }, [transactions, tenantTransactionId, selectedTransaction, fetchTimeline]);

  const handleTransactionSelect = (transaction: TransactionData) => {
    setSelectedTransaction(transaction);
    fetchTimeline(transaction.transactionId);
  };

  const handleTransactionCancelled = async () => {
    await refetch();
    setSelectedTransaction(null);
  };

  return (
    <motion.div
      className='relative w-[755px] max-h-[667px] bg-white rounded-[12px] flex flex-col overflow-auto max-h-[90vh] max-w-[90vw]'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <ModalHeader title='Past transactions' onClose={() => setModal(null)} />

      {transactions.length > 0 ? (
        <div className='flex flex-col md:flex-row h-[calc(667px-60px)] flex-grow'>
          <TransactionList
            transactions={transactions}
            selectedTransaction={selectedTransaction}
            isLoading={isLoading}
            leftSideRef={leftSideRef}
            onTransactionSelect={handleTransactionSelect}
          />
          <TransactionDetails
            transaction={selectedTransaction}
            timeline={timeline}
            timelineLoading={timelineLoading}
            onTransactionCancelled={() => void handleTransactionCancelled()}
          />
        </div>
      ) : isLoading ? (
        <div className='flex items-center justify-center flex-grow p-10'>Loading...</div>
      ) : (
        <EmptyState />
      )}
    </motion.div>
  );
};

export default PastTransactions;
